// The legacy in-Auth onboarding questionnaire (ONBOARDING_STEPS + the
// Onboarding component) was DELETED 2026-09-11 (write-path audit W15).
// It was unreachable — nothing ever set mode "onboarding", Scorify.jsx's
// 7-step flow owns onboarding, and no user ever completed it — and it
// carried three silent writes (profiles/body_stats/weight_log with an inert
// catch: supabase-js returns {error}, it does not throw) plus an
// onboarding_complete claim that would suppress the real flow if ever
// rewired. Do not resurrect it; the real flow lives in Scorify.jsx.
import { useState, useEffect } from "react";
import supabase from "./supabase";
import resetViewport from "./viewportReset";


const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,300&family=DM+Serif+Display:ital@0;1&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: #faf8f3; }
  ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); border-radius: 2px; }

  @media (prefers-reduced-motion: reduce){
    *,*::before,*::after{animation-duration:0.01ms !important;animation-iteration-count:1 !important;transition-duration:0.01ms !important;}
  }
  @keyframes fadeUp   { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn   { from{opacity:0} to{opacity:1} }
  @keyframes spin     { to{transform:rotate(360deg)} }
  @keyframes slideIn  { from{opacity:0;transform:translateX(32px)} to{opacity:1;transform:translateX(0)} }
  @keyframes pop      { 0%{transform:scale(0.9);opacity:0} 70%{transform:scale(1.03)} 100%{transform:scale(1);opacity:1} }

  .auth-fade  { animation: fadeUp 0.45s ease both; }
  .step-slide { animation: slideIn 0.35s ease both; }

  .auth-input {
    width: 100%;
    background: #ffffff;
    border: 1px solid #e8e1d3;
    border-radius: 10px;
    padding: 12px 16px;
    color: #1a1a1a;
    /* 16px minimum. Below 16, iOS zooms the webview when the field takes focus,
       and with the document pinned (body{position:fixed}) nothing unzooms it
       afterwards — the app stays scaled and shifted. Do not lower this. */
    font-size: 16px;
    font-family: 'DM Sans', sans-serif;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .auth-input:focus {
    border-color: #8b1e2d;
    box-shadow: 0 0 0 3px rgba(139,30,45,0.1);
  }
  .auth-input::placeholder { color: #5c574e; }

  .auth-btn {
    width: 100%;
    background: #8b1e2d;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 13px;
    font-size: 15px;
    font-weight: 700;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    letter-spacing: 0.01em;
    box-shadow: 0 4px 20px rgba(139,30,45,0.2);
    transition: opacity 0.15s;
  }
  .auth-btn:hover   { opacity: 0.88; }
  .auth-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .option-card {
    background: #ffffff;
    border: 1px solid rgba(0,0,0,0.08);
    border-radius: 12px;
    padding: 14px 18px;
    cursor: pointer;
    transition: all 0.15s;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #1a1a1a;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .option-card:hover   { border-color: rgba(139,30,45,0.4); background: rgba(139,30,45,0.04); }
  .option-card.selected {
    border-color: #8b1e2d;
    background: rgba(139,30,45,0.06);
    box-shadow: 0 0 0 1px #8b1e2d;
  }

  .progress-bar {
    height: 3px;
    background: rgba(0,0,0,0.08);
    border-radius: 99px;
    overflow: hidden;
    margin-bottom: 32px;
  }
  .progress-fill {
    height: 100%;
    background: #8b1e2d;
    border-radius: 99px;
    transition: width 0.4s ease;
  }

  .error-msg {
    background: rgba(192,57,43,0.08);
    border: 1px solid rgba(192,57,43,0.2);
    border-radius: 10px;
    padding: 10px 14px;
    font-size: 13px;
    color: #c0392b;
    font-weight: 500;
    animation: fadeIn 0.3s ease;
  }

  .link-btn {
    background: none;
    border: none;
    color: #8b1e2d;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
  }
  .link-btn:hover { opacity: 0.8; }

  button:disabled { cursor: not-allowed; }
`;

// ─── SHARED PAGE SHELL ────────────────────────────────────────────────────────
// Sign in / create account / forgot password all centre a card under AuthHeader.
//
// They used to do it with `minHeight: calc(100vh - 58px)` on the card row, which
// mis-centred on every notched iPhone: AuthHeader is `env(safe-area-inset-top) +
// 58px` tall, not 58px, so the row was taller than the space actually left and
// the card sat roughly half the inset too low — visibly more gap above than
// below. The padding was asymmetric (48 top, 60 bottom) on top of that.
//
// Now the page is a flex column and the card row takes `flex: 1`, so it centres
// in whatever the header really leaves, at any inset and any screen size. 100dvh
// rather than 100vh so the dynamic toolbar does not add phantom height.
const AUTH_PAGE = {
  minHeight: "100dvh",
  background: "#faf8f3",
  fontFamily: "'DM Sans', sans-serif",
  display: "flex",
  flexDirection: "column",
};
const AUTH_BODY = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 16px calc(24px + env(safe-area-inset-bottom, 0px))",
};

// ─── SHARED HEADER ────────────────────────────────────────────────────────────
function AuthHeader({ onBack, rightLabel, onRight }) {
  return (
    <div style={{
      background: "#faf8f3", borderBottom: "1px solid rgba(0,0,0,0.08)",
      // Longhand only. The old rule mixed the `padding` shorthand with a later
      // `paddingTop`; React re-applies shorthand and longhand in diff order on
      // update, so the safe-area padding could be wiped and the row would jam
      // against the status bar. minHeight now ADDS the inset to the 58px row
      // instead of letting the inset eat into it, which is why the bottom border
      // was landing through the "Scorify" title instead of beneath it.
      paddingLeft: 24, paddingRight: 24, paddingBottom: 12,
      paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
      minHeight: "calc(env(safe-area-inset-top, 0px) + 58px)",
      boxSizing: "border-box",
      // 1fr/auto/1fr grid, not space-between flex: with flex, unequal side
      // widths ("Back" vs "Create account") pushed the centered title off
      // center. Equal 1fr side tracks keep the title truly centered no matter
      // what the side content measures.
      display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center",
      position: "sticky", top: 0, zIndex: 50,
    }}>
      <button onClick={onBack} style={{
        background: "transparent", border: "none", cursor: "pointer",
        fontSize: 13, color: "#5c574e", fontWeight: 500,
        display: "flex", alignItems: "center", gap: 5,
        fontFamily: "'DM Sans', sans-serif", justifySelf: "start",
      }}>
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
        Back
      </button>
      <div style={{ fontSize: 18, fontWeight: 700, color: "#1a1a1a", letterSpacing: "-0.02em", fontFamily: "'DM Sans', sans-serif" }}>Scorify</div>
      {onRight
        ? <button className="link-btn" onClick={onRight} style={{ justifySelf: "end" }}>{rightLabel}</button>
        : <div style={{ justifySelf: "end" }} />
      }
    </div>
  );
}

// ─── FIELD LABEL ─────────────────────────────────────────────────────────────
function FieldLabel({ children }) {
  return (
    <div style={{ fontSize: 12, color: "#5c574e", fontWeight: 600, marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.06em" }}>
      {children}
    </div>
  );
}

// ─── SIGN UP ─────────────────────────────────────────────────────────────────
function SignUp({ onSwitch, onSuccess, onBack }) {
  const [username, setUsername] = useState("");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const passwordStrength = (p) => {
    if (!p) return null;
    if (p.length < 6)  return { label: "Too short", color: "#c0392b", width: "20%" };
    if (p.length < 8)  return { label: "Weak",      color: "#c0392b", width: "35%" };
    const score = [/[A-Z]/.test(p), /[0-9]/.test(p), /[^A-Za-z0-9]/.test(p)].filter(Boolean).length;
    if (score === 0)   return { label: "Fair",   color: "#8b1e2d", width: "50%" };
    if (score === 1)   return { label: "Good",   color: "#8b1e2d", width: "70%" };
    return               { label: "Strong", color: "#2d6a4f", width: "100%" };
  };
  const strength = passwordStrength(password);

  const handleSignUp = async () => {
    if (!username.trim())                        { setError("Username is required."); return; }
    if (username.length < 3)                     { setError("Username must be at least 3 characters."); return; }
    if (!/^[a-zA-Z0-9_]+$/.test(username))       { setError("Username can only contain letters, numbers, and underscores."); return; }
    if (!email.trim())                           { setError("Email is required."); return; }
    if (password.length < 8)                     { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm)                    { setError("Passwords don't match."); return; }

    setError(""); setLoading(true);
    try {
      const { data: existing } = await supabase.from("profiles").select("username").eq("username", username.toLowerCase()).single();
      if (existing) { setError("That username is taken. Try another."); setLoading(false); return; }

      const { data, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { username: username.toLowerCase() } },
      });
      if (authError) { setError(authError.message); setLoading(false); return; }
      // Fire-and-forget: add to Resend profiles audience.
      //
      // The route now derives the address from the verified token, so the email
      // is no longer sent in the body — it only needs the bearer token. The
      // token is read from the signUp response rather than a follow-up
      // getSession() call so it cannot race the client persisting the session.
      //
      // data.session is null when email confirmation is enabled (it is not
      // today). Skipping is correct in that case: the contact gets added on
      // first real sign-in rather than the call 401ing forever.
      const signupToken = data?.session?.access_token;
      if (signupToken) {
        fetch("/api/add-contact", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${signupToken}`,
          },
          body: JSON.stringify({}),
        }).catch(() => {});
      } else {
        console.warn("add-contact skipped: no session on signUp (email confirmation enabled?)");
      }
      // Fresh signups get the app lock offer as an ONBOARDING step; this flag
      // stops LockGate's one-time prompt (meant for accounts that predate the
      // lock) from stacking on top of the onboarding modal.
      try { localStorage.setItem('scorify_lock_prompt_done', 'onboarding'); } catch {}
      onSuccess(data.user);
    } catch { setError("Something went wrong. Try again."); }
    setLoading(false);
  };

  return (
    <div style={AUTH_PAGE}>
      <AuthHeader onBack={onBack} rightLabel="Sign in" onRight={onSwitch} />
      <div style={AUTH_BODY}>
        <div className="auth-fade" style={{
          background: "#ffffff", border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: 20, padding: "40px 36px",
          width: "100%", maxWidth: 420,
          boxShadow: "0 8px 40px rgba(0,0,0,0.07)",
        }}>
          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 30, fontWeight: 400, letterSpacing: "-0.02em", color: "#1a1a1a", marginBottom: 8 }}>
              Create your account
            </h1>
            <p style={{ fontSize: 14, color: "#5c574e", lineHeight: 1.5 }}>See where you stand in minutes. Free, forever.</p>
          </div>

          {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <FieldLabel>Username</FieldLabel>
              <input className="auth-input" placeholder="your_handle" value={username}
                onChange={e => { setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, "")); setError(""); }} />
              <div style={{ fontSize: 11, color: "#5c574e", marginTop: 5 }}>Letters, numbers, and underscores only.</div>
            </div>
            <div>
              <FieldLabel>Email</FieldLabel>
              <input className="auth-input" type="email" placeholder="you@example.com" value={email}
                onChange={e => { setEmail(e.target.value); setError(""); }} />
            </div>
            <div>
              <FieldLabel>Password</FieldLabel>
              <input className="auth-input" type="password" placeholder="Min. 8 characters" value={password}
                onChange={e => { setPassword(e.target.value); setError(""); }} autoComplete="new-password" />
              {strength && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ background: "rgba(0,0,0,0.08)", borderRadius: 99, height: 4, overflow: "hidden" }}>
                    <div style={{ width: strength.width, background: strength.color, height: "100%", borderRadius: 99, transition: "width 0.3s ease" }} />
                  </div>
                  <div style={{ fontSize: 11, color: strength.color, fontWeight: 600, marginTop: 4 }}>{strength.label}</div>
                </div>
              )}
            </div>
            <div>
              <FieldLabel>Confirm password</FieldLabel>
              <input className="auth-input" type="password" placeholder="••••••••" value={confirm}
                onChange={e => { setConfirm(e.target.value); setError(""); }}
                onKeyDown={e => e.key === "Enter" && handleSignUp()}
                autoComplete="new-password"
                style={{ borderColor: confirm && confirm !== password ? "rgba(192,57,43,0.4)" : undefined }} />
              {confirm && confirm !== password && (
                <div style={{ fontSize: 11, color: "#c0392b", marginTop: 5, fontWeight: 500 }}>Passwords don't match</div>
              )}
            </div>

            <button className="auth-btn" onClick={handleSignUp} disabled={loading} style={{ marginTop: 6 }}>
              {loading
                ? <span style={{ display: "inline-block", width: 18, height: 18, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                : "Create account →"}
            </button>
          </div>

          {/* What you get */}
          <div style={{ marginTop: 24, padding: "14px 16px", background: "#faf8f3", borderRadius: 12, border: "1px solid rgba(0,0,0,0.07)" }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "#5c574e", marginBottom: 10 }}>What you get</div>
            {["Four pillars, scored separately", "Habit streaks + templates", "Finance & credit dashboard", "Mood tracking + AI coach"].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: i < 3 ? 7 : 0 }}>
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#2d6a4f" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                <span style={{ fontSize: 13, color: "#1a1a1a", fontWeight: 500 }}>{item}</span>
              </div>
            ))}
          </div>

          <div style={{ textAlign: "center", marginTop: 20 }}>
            <span style={{ fontSize: 13, color: "#5c574e" }}>Already have an account? </span>
            <button className="link-btn" onClick={onSwitch}>Sign in →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SIGN IN ─────────────────────────────────────────────────────────────────
function SignIn({ onSwitch, onSuccess, onBack, onDemo, onForgot }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  // Set by apiFetch's session-expired handoff (Scorify.jsx): explain why the
  // user was returned here instead of leaving them guessing after a silent bounce.
  const [notice, setNotice]     = useState("");
  useEffect(() => {
    try {
      if (localStorage.getItem("scorify_session_expired")) {
        setNotice("Your session expired. Please sign in again.");
        localStorage.removeItem("scorify_session_expired");
      }
    } catch {}
  }, []);

  const handleSignIn = async () => {
    if (!email.trim() || !password.trim()) { setError("Please fill in all fields."); return; }
    setError(""); setLoading(true);
    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) { setError("Invalid email or password."); setLoading(false); return; }
    onSuccess(data.user);
    setLoading(false);
  };

  return (
    <div style={AUTH_PAGE}>
      <AuthHeader onBack={onBack} rightLabel="Create account" onRight={onSwitch} />
      <div style={AUTH_BODY}>
        <div className="auth-fade" style={{
          background: "#ffffff", border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: 20, padding: "40px 36px",
          width: "100%", maxWidth: 420,
          boxShadow: "0 8px 40px rgba(0,0,0,0.07)",
        }}>
          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 30, fontWeight: 400, letterSpacing: "-0.02em", color: "#1a1a1a", marginBottom: 8 }}>
              Welcome back
            </h1>
            <p style={{ fontSize: 14, color: "#5c574e", lineHeight: 1.5 }}>Sign in to pick up where you left off.</p>
          </div>

          {notice && !error && <div style={{ marginBottom: 20, fontSize: 13.5, color: "#5c574e", background: "#f5f2ea", border: "1px solid #e8e1d3", borderRadius: 10, padding: "10px 14px", lineHeight: 1.5 }}>{notice}</div>}
          {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <FieldLabel>Email</FieldLabel>
              {/* No autoFocus on the auth screens: programmatic focus during
                  the entrance fade raced the focus-zoom clamp and iOS zoomed
                  toward the field, then snapped back (intermittent). The
                  guard now also intercepts focus() itself, but the keyboard
                  bursting in mid-transition was never good entrance anyway. */}
              <input className="auth-input" type="email" placeholder="you@example.com" value={email}
                onChange={e => { setEmail(e.target.value); setError(""); }} />
            </div>
            <div>
              <FieldLabel>Password</FieldLabel>
              <input className="auth-input" type="password" placeholder="••••••••" value={password}
                onChange={e => { setPassword(e.target.value); setError(""); }}
                onKeyDown={e => e.key === "Enter" && handleSignIn()} />
              <div style={{ textAlign: "right", marginTop: 6 }}>
                <button className="link-btn" style={{ fontSize: 12 }} onClick={onForgot}>Forgot password?</button>
              </div>
            </div>

            <button className="auth-btn" onClick={handleSignIn} disabled={loading} style={{ marginTop: 6 }}>
              {loading
                ? <span style={{ display: "inline-block", width: 18, height: 18, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                : "Sign in →"}
            </button>
          </div>

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "22px 0 18px" }}>
            <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,0.08)" }} />
            <span style={{ fontSize: 12, color: "#5c574e" }}>or</span>
            <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,0.08)" }} />
          </div>

          <button onClick={onDemo} style={{
            width: "100%", background: "#f5f2ea",
            border: "1px solid rgba(0,0,0,0.08)", borderRadius: 10,
            padding: "12px 20px", cursor: "pointer",
            fontWeight: 600, fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: "#1a1a1a",
          }}>Try the demo</button>

          <div style={{ textAlign: "center", marginTop: 20 }}>
            <span style={{ fontSize: 13, color: "#5c574e" }}>Don't have an account? </span>
            <button className="link-btn" onClick={onSwitch}>Create one →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── FORGOT PASSWORD ─────────────────────────────────────────────────────────
function ForgotPassword({ onBack, onBackToSignIn }) {
  const [email, setEmail]     = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [sent, setSent]       = useState(false);

  const handleReset = async () => {
    if (!email.trim()) { setError("Please enter your email address."); return; }
    setError(""); setLoading(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: "https://scorify.to/reset-password",
    });
    setLoading(false);
    if (resetError) { setError(resetError.message); return; }
    setSent(true);
  };

  return (
    <div style={AUTH_PAGE}>
      <AuthHeader onBack={onBack} rightLabel="Sign in" onRight={onBackToSignIn} />
      <div style={AUTH_BODY}>
        <div className="auth-fade" style={{
          background: "#ffffff", border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: 20, padding: "40px 36px",
          width: "100%", maxWidth: 420,
          boxShadow: "0 8px 40px rgba(0,0,0,0.07)",
        }}>
          {sent ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(45,106,79,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#2d6a4f" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
              </div>
              <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, fontWeight: 400, letterSpacing: "-0.02em", color: "#1a1a1a", marginBottom: 12 }}>
                Check your email
              </h1>
              <p style={{ fontSize: 14, color: "#5c574e", lineHeight: 1.6, marginBottom: 28 }}>
                We sent a reset link to <strong style={{ color: "#1a1a1a" }}>{email.trim()}</strong>. Click the link to set a new password.
              </p>
              <button className="link-btn" onClick={onBackToSignIn}>← Back to sign in</button>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 28 }}>
                <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 30, fontWeight: 400, letterSpacing: "-0.02em", color: "#1a1a1a", marginBottom: 8 }}>
                  Reset your password
                </h1>
                <p style={{ fontSize: 14, color: "#5c574e", lineHeight: 1.5 }}>Enter your email and we'll send you a link to reset your password.</p>
              </div>

              {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <FieldLabel>Email</FieldLabel>
                  <input className="auth-input" type="email" placeholder="you@example.com" value={email}
                    onChange={e => { setEmail(e.target.value); setError(""); }}
                    onKeyDown={e => e.key === "Enter" && handleReset()} />
                </div>
                <button className="auth-btn" onClick={handleReset} disabled={loading} style={{ marginTop: 6 }}>
                  {loading
                    ? <span style={{ display: "inline-block", width: 18, height: 18, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                    : "Send reset link"}
                </button>
              </div>

              <div style={{ textAlign: "center", marginTop: 24 }}>
                <button className="link-btn" onClick={onBackToSignIn}>← Back to sign in</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN AUTH WRAPPER ────────────────────────────────────────────────────────
export default function Auth({ onAuthenticated, onBack, onDemo }) {
  // Sign in is the default: every entry point into /login except one is a
  // returning user (the landing header, the waitlist page, the password-reset
  // confirmation, the demo banner's "Sign in"). The exception is the demo
  // banner's "Create Account", which asks for signup explicitly via ?mode=signup.
  const [mode, setMode]                   = useState(
    () => new URLSearchParams(window.location.search).get("mode") === "signup" ? "signup" : "signin"
  );

  // Onboarding is owned by the 7-step flow in Scorify.jsx, which is what reads and
  // writes profiles.onboarding_complete. This legacy questionnaire used to claim the
  // same flag on sign in, which would mark a user complete before the real flow ever
  // ran — permanently suppressing it (no user has ever completed it: age_range is
  // null for all rows). Go straight to the app and let Scorify decide.
  // The keyboard closing does not undo a zoom or pan — that state lives in the
  // webview, not in React, so it survives this screen unmounting and the app
  // renders scaled and shifted. Reset when the keyboard goes away, when we hand
  // off to the app, and on unmount.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onViewportChange = () => {
      const el = document.activeElement;
      const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA");
      // Keyboard is gone (viewport is full height again) and nothing is focused.
      // SCALE-AWARE on purpose (sign-in zoom regression, 2026-09-14): vv.height
      // is in visual-viewport CSS px, so at zoom scale s it reads
      // ~innerHeight/s with NO keyboard on screen. The old test
      // (innerHeight - vv.height - vv.offsetTop < 80) therefore read a zoomed
      // page as "keyboard still open" and never fired — which is exactly how a
      // stray focus-zoom became permanent on the sign-in screen. Multiplying
      // by scale rebuilds the keyboard occlusion in layout px: identical
      // verdicts at scale 1, and a zoomed keyboard-less page now resets.
      if (!typing && window.innerHeight - vv.height * (vv.scale || 1) < 80) {
        if ((vv.scale || 1) > 1.01 || vv.offsetLeft > 0 || vv.offsetTop > 0) resetViewport();
      }
    };
    vv.addEventListener("resize", onViewportChange);
    vv.addEventListener("scroll", onViewportChange);
    return () => {
      vv.removeEventListener("resize", onViewportChange);
      vv.removeEventListener("scroll", onViewportChange);
      resetViewport();
    };
  }, []);

  const handleAuthSuccess = async (authUser) => {
    resetViewport();
    onAuthenticated(authUser);
  };



  if (mode === "forgot") {
    return (
      <>
        <style>{STYLES}</style>
        <ForgotPassword onBack={() => setMode("signin")} onBackToSignIn={() => setMode("signin")} />
      </>
    );
  }

  return (
    <>
      <style>{STYLES}</style>
      {mode === "signup"
        ? <SignUp onSwitch={() => setMode("signin")} onSuccess={handleAuthSuccess} onBack={onBack} />
        : <SignIn onSwitch={() => setMode("signup")} onSuccess={handleAuthSuccess} onBack={onBack} onDemo={onDemo} onForgot={() => setMode("forgot")} />
      }
    </>
  );
}
